/**
 * Centralized error handler for authentication flows
 * Provides consistent error messaging across the application
 */

export interface AuthError {
  response?: {
    status?: number;
    data?: any;
  };
  message?: string;
  code?: string;
}

/**
 * Extracts user-friendly error message from authentication API errors
 * @param error - The error object from API response
 * @param fallbackMessage - Default message if no specific error is found
 * @returns User-friendly error message
 */
export const getAuthErrorMessage = (error: AuthError, fallbackMessage = "An error occurred. Please try again later."): string => {
  // Handle network errors
  if (!error.response && error.message) {
    if (error.message.includes('Network Error') || error.message.includes('ERR_NETWORK')) {
      return "Network connection failed. Please check your internet connection.";
    }
    return error.message;
  }

  // Handle response errors
  if (error.response) {
    const { status, data } = error.response;

    // If data is a string, return it directly (most backend errors are strings)
    if (typeof data === 'string' && data.trim()) {
      return data;
    }

    // Handle structured error responses
    if (data && typeof data === 'object') {
      if (data.message) return data.message;
      if (data.error) return data.error;
      if (data.detail) return data.detail;
    }

    // Handle HTTP status codes with fallback messages
    if (status !== undefined) {
      switch (status) {
        case 400:
          return "Invalid request. Please check your input and try again.";
        case 401:
          return "Authentication failed. Please check your credentials.";
        case 403:
          return "Access denied. Please check your credentials or permissions.";
        case 404:
          return "The requested resource was not found.";
        case 409:
          return "A conflict occurred. Please check your input.";
        case 422:
          return "Validation failed. Please check your input.";
        case 429:
          return "Too many requests. Please wait a moment and try again.";
        case 500:
          return "Server error occurred. Please try again later.";
        case 502:
          return "Service temporarily unavailable. Please try again later.";
        case 503:
          return "Service unavailable. Please try again later.";
        default:
          if (status >= 400 && status < 500) {
            return "Client error occurred. Please check your request.";
          }
          if (status >= 500) {
            return "Server error occurred. Please try again later.";
          }
      }
    }
  }

  return fallbackMessage;
};

/**
 * Maps specific backend error messages to user-friendly messages
 * Used for customizing specific error messages beyond the generic handler
 */
export const AUTH_ERROR_MESSAGES = {
  // Authentication errors
  'Invalid credentials': 'Invalid email or password. Please check your credentials and try again.',
  'Invalid authorization data': 'Your session has expired. Please log in again.',
  'Invalid email code': 'The email verification code is incorrect. Please check and try again.',
  'Invalid sms code': 'The SMS verification code is incorrect. Please check and try again.',
  'Invalid 2fa code': 'The two-factor authentication code is incorrect. Please check and try again.',
  
  // Registration errors
  'Email already exists.': 'This email address is already registered. Please use a different email or try logging in.',
  'Email already exist': 'This email address is already registered. Please use a different email or try logging in.',
  'This email already exist': 'This email address is already registered. Please use a different email or try logging in.',
  'Phone already exist': 'This phone number is already registered. Please use a different phone number.',
  'Invalid password': 'Password does not meet requirements. Please ensure it has at least 8 characters with uppercase, lowercase, and numbers.',
  'Invalid password format': 'Password does not meet requirements. Please ensure it has at least 8 characters with uppercase, lowercase, and numbers.',
  
  // Password reset errors
  'Invalid email': 'Email address not found. Please check the email address and try again.',
  'The link is not valid': 'The password reset link is invalid. Please request a new password reset.',
  'The link has been expired': 'The password reset link has expired. Please request a new password reset.',
  'Need 2Fa Code': 'Two-factor authentication code is required. Please enter your 2FA code.',
  'The new password must be different from the existing password.': 'The new password must be different from your current password.',
  
  // reCAPTCHA errors
  'reCAPTCHA verification failed': 'Security verification failed. Please refresh the page and try again.',
  
  // General errors
  'User not found': 'User account not found. Please check your information and try again.',
  'Invalid verify code': 'The verification code is incorrect. Please check and try again.',
  'Invalid arguments': 'Required information is missing. Please fill in all required fields.',
  'Invalid old password': 'Current password is incorrect. Please check and try again.',
  'New and old passwords are the same': 'New password must be different from your current password.',
} as const;

/**
 * Gets a user-friendly error message with custom mapping support
 * @param error - The error object from API response
 * @param fallbackMessage - Default message if no specific error is found
 * @returns User-friendly error message
 */
export const getCustomAuthErrorMessage = (error: AuthError, fallbackMessage = "An error occurred. Please try again later."): string => {
  const genericMessage = getAuthErrorMessage(error, fallbackMessage);
  
  // Check if we have a custom message for this specific error
  if (AUTH_ERROR_MESSAGES[genericMessage as keyof typeof AUTH_ERROR_MESSAGES]) {
    return AUTH_ERROR_MESSAGES[genericMessage as keyof typeof AUTH_ERROR_MESSAGES];
  }
  
  return genericMessage;
};

/**
 * Type guard to check if an error is an auth error
 */
export const isAuthError = (error: any): error is AuthError => {
  return error && (error.response || error.message);
};
